import styled from '@emotion/styled';
import type { PropsOf } from '@emotion/react';
import Quill from 'quill';
import { useCallback, useEffect, useRef, useState } from 'react';
import { tokens } from '@hotjar/hotsauce';

// the history property with the clear method is not defined in the types package for the quill library
interface QuillEditor extends Quill {
  history: {
    clear: () => void;
  };
}

export interface TextEditorProps extends Omit<PropsOf<'div'>, 'value' | 'onChange'> {
  value: string;
  isInvalid?: boolean;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const TextEditorContainer = styled.div<Pick<TextEditorProps, 'isInvalid'>>`
  .ql-toolbar {
    border-radius: 4px 4px 0 0;
  }

  .ql-container {
    border-radius: 0 0 4px 4px;
  }

  .ql-hidden * {
    display: none;
  }
  ${({ isInvalid }) =>
    isInvalid === true &&
    `
    .ql-toolbar {
      border-top-color: ${tokens.color.ui_border_danger};
      border-left-color: ${tokens.color.ui_border_danger};
      border-right-color: ${tokens.color.ui_border_danger};
    }

    .ql-container{
      border-color: ${tokens.color.ui_border_danger};
    }
  `}
`;

const isEmpty = (value: string) => /^(?:<\/?p>|<br>| |\t)*$/.test(value);

export const TextEditor = ({
  value,
  onChange,
  onFocus,
  onBlur,
  ...containerProps
}: TextEditorProps): JSX.Element => {
  const [editor, setEditor] = useState<QuillEditor>();
  const contentRef = useRef<string>();

  useEffect(() => {
    if (editor && value !== contentRef.current) {
      // setContents resets the editor, so only call this when the value has changed externally
      editor.setContents(editor.clipboard.convert(value));
      editor.history.clear();
    }
  }, [editor, value]);

  // Set up the quill editor.
  const editorContainerRef = useCallback(
    (node: HTMLDivElement) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (node !== null && !editor) {
        const quill = new Quill(node, {
          theme: 'snow',
          modules: {
            clipboard: {
              matchVisual: false,
            },
            toolbar: [['bold', 'italic', 'underline', 'strike'], ['link'], ['clean']],
          },
        }) as QuillEditor;

        quill.root.setAttribute('role', 'textbox');
        quill.root.setAttribute('aria-multiline', 'true');

        quill.on('text-change', () => {
          const html = quill.root.innerHTML;
          const newValue = isEmpty(html) ? '' : html;
          contentRef.current = newValue;
          onChange(newValue);
        });

        quill.on('selection-change', (range, oldRange) => {
          // Looking at the quill source, it seems like the type information might not be correct and range can be null
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (range !== null && oldRange === null && onFocus) onFocus();
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          else if (range === null && oldRange !== null && onBlur) onBlur();
        });

        setEditor(quill);
      }
    },
    [onChange, editor, onFocus, onBlur],
  );

  return (
    <TextEditorContainer {...containerProps}>
      <div ref={editorContainerRef} />
    </TextEditorContainer>
  );
};
